import { RefObject, useEffect, useRef } from 'react'
import { useEventListener } from './useEventListener'

// This List is Not Exhaustive, but Native Keyboard Event
// does not display a specific type
// prettier-ignore
type key = '/'| 'Escape' | 'Space' | 'Enter' | 'Control' | 'Alt' | 'Option' | 'Command' | 'Meta' | 'Shift' | 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp' | 'Tab' | 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h' | 'i' | 'j' | 'k' | 'l' | 'm' | 'n' | 'o' | 'p' | 'q' | 'r' | 's' | 't' | 'u' | 'v' | 'w' | 'x' | 'y' | 'z'

// Single key = match ignoring modifiers; multi-key = combo (modifiers first, main key last).
// Control/Command/Meta collapse to Cmd-or-Ctrl for cross-platform compat.
const isHotkeyMatch = (hotkey: string[], e: KeyboardEvent) => {
  if (hotkey.length === 0) return false
  if (hotkey.length === 1) return e.key === hotkey[0]

  const mainKey = hotkey[hotkey.length - 1]
  const modifiers = hotkey.slice(0, -1)

  if (e.key.toLowerCase() !== mainKey.toLowerCase()) return false

  // Lowercase the modifier names so this works whether the hotkey came
  // from a user-authored array (`'Control'`) or was round-tripped through
  // the stack signature.
  const mods = modifiers.map((m) => m.toLowerCase())
  const wantCmdOrCtrl = mods.some(
    (m) => m === 'control' || m === 'command' || m === 'meta',
  )
  const wantAlt = mods.some((m) => m === 'alt' || m === 'option')
  const wantShift = mods.includes('shift')

  return (
    wantCmdOrCtrl === (e.ctrlKey || e.metaKey) &&
    wantAlt === e.altKey &&
    wantShift === e.shiftKey
  )
}

// Window-scoped LIFO stack: most-recently-mounted binding wins; older ones resume when it
// unmounts. `fireEvenIfOverridden: true` opts out of suppression.

const makeEntry = (handler: () => void, fireEvenIfOverridden: boolean) => ({
  id: Symbol(),
  handler,
  fireEvenIfOverridden,
  disabled: false,
})
type Entry = ReturnType<typeof makeEntry>

const stacks = new Map<string, Entry[]>()

// Signature preserves case so single-key match sees the original "Escape"/"Enter".
const signatureFor = (hotkey: string[]) => hotkey.join('+')
const parseSignature = (sig: string) => sig.split('+')

const dispatchKeydown = (e: KeyboardEvent) => {
  stacks.forEach((entries, sig) => {
    if (!isHotkeyMatch(parseSignature(sig), e)) return

    const live = entries.filter((entry) => !entry.disabled)
    if (live.length === 0) return

    e.preventDefault()

    // Always-fire bindings run regardless of who owns the top.
    live
      .filter((entry) => entry.fireEvenIfOverridden)
      .forEach((entry) => entry.handler())

    // Plus the single most-recently-mounted normal binding.
    const normal = live.filter((entry) => !entry.fireEvenIfOverridden)
    normal[normal.length - 1]?.handler()
  })
}

// Attach the single window listener once on first registration (module singleton, no cleanup).
const ensureDispatcher = (() => {
  let attached = false
  return () => {
    if (attached) return
    window.addEventListener('keydown', dispatchKeydown)
    attached = true
  }
})()

const registerWindowEntry = (
  sig: string,
  handler: () => void,
  fireEvenIfOverridden: boolean,
) => {
  const entry = makeEntry(handler, fireEvenIfOverridden)
  const existing = stacks.get(sig) ?? []
  stacks.set(sig, [...existing, entry])
  ensureDispatcher()
  return entry
}

const removeWindowEntry = (sig: string, entry: Entry) => {
  const remaining = (stacks.get(sig) ?? []).filter((e) => e.id !== entry.id)
  if (remaining.length === 0) stacks.delete(sig)
  else stacks.set(sig, remaining)
}

/**
 * Test-only — wipes the module-level stack so each test starts from a
 * known state. Not exported via the package barrel; specs import the
 * hook file directly.
 */
export const __resetKeybindStackForTests = () => {
  stacks.clear()
}

/**
 * Calls a function when a key or series of keys are pressed. Optionally
 * scoped to a single component via `ref`.
 *
 * Window-scoped bindings (no ref) participate in a LIFO stack: when two
 * components bind the same hotkey, only the most recently mounted one fires.
 * Pass `{ fireEvenIfOverridden: true }` to opt a binding out of that
 * suppression.
 *
 * @param hotkey array of keys that make up the hotkey. For combos, list
 *   modifiers first and the main key last, e.g. `['Control', 'p']`.
 *   Control / Command / Meta collapse to "Cmd-or-Ctrl" so the same binding
 *   works on Mac and Windows/Linux.
 * @param onPress function to call when the hotkey is pressed.
 * @param ref optional element ref. When provided, the listener is attached
 *   to that element and does not participate in the window stack.
 * @param disableWhen when true the binding is suppressed (kept in the stack
 *   at its current position but skipped on dispatch). Toggling this back to
 *   false restores the binding without bumping it to the top of the stack.
 * @param options.fireEvenIfOverridden when true the binding fires on every
 *   matching keypress, regardless of whether something later-mounted is on
 *   top of the stack. Use for app-wide shortcuts that must work even with a
 *   modal layered above (e.g. global Cmd+P palette opener). No effect for
 *   element-scoped bindings — those never participate in the stack.
 *
 * @example
 *   useKeybind(['Escape'], () => setOpen(false))
 *   useKeybind(['Enter'], submit, buttonRef)
 *   useKeybind(['Control', 'p'], togglePalette, undefined, false, {
 *     fireEvenIfOverridden: true,
 *   })
 */
export const useKeybind = <T extends RefObject<HTMLElement | null> | null>(
  hotkey: key[],
  onPress: () => void,
  ref?: T,
  disableWhen: boolean = false,
  options: { fireEvenIfOverridden?: boolean } = {},
) => {
  const isElementScoped = !!ref
  const { fireEvenIfOverridden = false } = options

  // Stable ref so handler changes don't re-push to the stack; update each render.
  const savedHandler = useRef(onPress)
  // No deps: refresh the ref to the latest onPress each render without re-subscribing.
  useEffect(() => {
    savedHandler.current = onPress
  })

  // Element-scoped: own listener via useEventListener, never touches the shared stack.
  const elementHandlerRef = useRef((e: KeyboardEvent) => {
    if (!isHotkeyMatch(hotkey, e)) return
    e.preventDefault()
    savedHandler.current()
  })
  // Refresh the wrapped hotkey so a hotkey-array change still matches.
  elementHandlerRef.current = (e: KeyboardEvent) => {
    if (!isHotkeyMatch(hotkey, e)) return
    e.preventDefault()
    savedHandler.current()
  }
  useEventListener(
    'keydown',
    (e) => elementHandlerRef.current(e),
    ref?.current,
    disableWhen || !isElementScoped,
  )

  // Window-scoped: push once, mutate in place; deps are sig/scope/opt-out only to preserve LIFO.
  const sig = signatureFor(hotkey)
  const entryRef = useRef<Entry | null>(null)
  // Register the window keybind entry on the LIFO stack (cleanup removes it); skip if element-scoped.
  useEffect(() => {
    if (isElementScoped) return
    const entry = registerWindowEntry(
      sig,
      () => savedHandler.current(),
      fireEvenIfOverridden,
    )
    entryRef.current = entry
    return () => {
      removeWindowEntry(sig, entry)
      entryRef.current = null
    }
  }, [sig, isElementScoped, fireEvenIfOverridden])

  // Re-registering (the alternative) would push the binding to the top of
  // the stack on each enable toggle; flipping a flag in place preserves the
  // entry's original LIFO position across enable/disable cycles.
  useEffect(() => {
    if (entryRef.current) entryRef.current.disabled = disableWhen
  }, [disableWhen])
}
